<template>
  <div class="create-url">
    <div class="create-url__title">Create New URL</div>
    <div class="create-url__header">

      <div class="create-url__title-wrapper">
        <div class="create-url__info-item" :style="elementStyle">
          <div class="create-url__header-title">Target Date</div>
          <base-date-picker-button
            class="create-url__date"
            v-model="url.date"
          ></base-date-picker-button>
        </div>
        <div class="create-url__info-item" :style="elementStyle">
          <div class="create-url__header-title">URL Status</div>
          <div class="create-url__status">Proposed</div>
        </div>
        <div class="create-url__info-item" :style="elementStyle">
          <div class="create-url__header-title">Section</div>
          <base-select
            class="create-url__section"
            border-less="true"
            :options="sections"
            v-model="url.sectionId"
            alignLeft="true"
            :max-height-list="450"
            :titleStyle="titleStyle"
            :headerStyle="selectSectionStyle"
          />
        </div>
        <div class="create-url__info-item" :style="elementStyle">
          <div class="create-url__header-title">Page Type</div>
          <base-select
            border-less="true"
            class="create-url__page-type"
            :options="pageTypes"
            v-model="url.pageTypeId"
            alignLeft="true"
            :max-height-list="450"
            :titleStyle="titleStyle"
            :headerStyle="selectTypeStyle"
          />
        </div>
      </div>
    </div>

    <label class="create-url__input">
      <span class="create-url__label">URL</span>
      <div v-if="!isDomainDropdownHidden">
        <base-select
          v-if="!isSingleDomainName"
          :options="domainNames"
          v-model="url.domain"
          :max-height-list="250"
          placeholder="Choose Domain"
          :headerStyle="selectDomainStyle"
        />
        <span v-else class="create-url__domain">{{domainNames[0].label}}</span>
      </div>
      <div class="create-url__link-wrapper" :class="{'create-url__link-wrapper_single': isDomainDropdownHidden}">
        <base-smart-input-autocomplete
          easyModel
          class="form-control"
          :borderLess="true"
          placeholder="Enter second path segment of the url"
          :class="{'form-control_error': isPathError || isPathDuplicateError || externalError}"
          :inputStyles="{
            'padding-left': '0'
          }"
          :options="autocompleteFilters"
          v-model="url.path"/>
        <span class="create-url__error-message" v-if="externalError">{{externalError}}</span>
        <span class="create-url__error-message" v-if="isPathError">Path is not valid</span>
        <span class="create-url__error-message" v-else-if="isPathDuplicateError">This url already exists</span>
      </div>
    </label>
    <div class="create-url__controls">
      <button class="create-url__button create-url__button_cancel"
              @click.prevent="close()">Cancel
      </button>
      <button class="create-url__button create-url__button_submit"
              @click.prevent="apply()">
        Apply
      </button>
    </div>
  </div>
</template>

<script>
import Filters from '@/common/Filters'
import { mapActions, mapState } from 'vuex'
import validator from 'validator'
import BaseModalMixins from '@/common/BaseModalMixins'

const SELECT_STYLE = {
  'background-color': 'transparent',
  color: '#284aff',
  'text-decoration': 'underline'
}

/**
 * Popup is shown when we want to add new URL
 */
export default {
  name: 'CreateUrlPopup',
  mixins: [BaseModalMixins],
  props: {
    urls: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      url: {
        pageTypeId: null,
        sectionId: null,
        date: null,
        domain: null,
        path: null
      },
      externalError: null,
      isValidating: false,
      // should domain name selector be hidden or not
      isDomainDropdownHidden: true,
      elementStyle: {},
      titleStyle: {
        overflow: 'visible'
      }
    }
  },
  computed: {
    ...mapState('urlCensus', {
      autocompleteFilters (state) {
        return (state.urlExamples
          .filter(v => v?.toLowerCase().includes((this.url.path || '')?.toLowerCase())) || [])
          .map(v => ({ value: v, label: v }))
          .sort((a, b) => a.label.localeCompare(b.label))
      }
    }),
    ...mapState('rankZoneCommon', {
      sections (state) {
        const sections = state.sections.map(this.convertToOptions) || []
        sections.unshift({
          label: 'Choose Section',
          value: null
        })
        return sections
      },
      urlStatuses (state) {
        const statuses = state.urlStatuses.map(this.convertToOptions) || []
        statuses.unshift({
          label: 'Choose Status',
          value: null
        })
        return statuses
      },
      pageTypes (state) {
        const types = state.pageTypes.map(this.convertToOptions) || []
        // add default value
        this.url.pageTypeId = types[0].value
        return types
      },
      selectSectionStyle () {
        return SELECT_STYLE
      },
      selectTypeStyle () {
        return SELECT_STYLE
      },
      selectDomainStyle () {
        const color = !this.url.domain && this.isValidating ? '#e11c21' : '#011627'
        return {
          'background-color': 'rgba(1,22,39,.1)',
          color
        }
      },
      /**
       * if only 1 domain name presents we should disable selection mode
       * */
      isSingleDomainName (state) {
        return this.domainNames && this.domainNames.length === 1
      },
      /**
       * we use as domainNames only web property data with type 'clients' and valid as URL
       * */
      domainNames (state) {
        const domains = state.webProperties
          .filter(item => item.type?.toLowerCase() === 'clients')
          .filter(item => validator.isURL(item.domain))
          .map(item => {
            return {
              value: item.domain,
              label: item.domain
            }
          }) || []
        // if only 1 domain name presents - we show this one domain name
        if (domains.length === 1) {
          this.url.domain = domains[0].label
        }
        return domains
      }
    }),
    isPathError () {
      return this.isValidating && (this.isUrlNotValid())
    },
    isPathDuplicateError () {
      return !this.isUrlNotValid() && this.isUrlAlreadyExist()
    },
    domain () {
      return this.isDomainDropdownHidden ? '' : this.url.domain
    }
  },
  methods: {
    /**
     * Handling decline creating new URL & hiding component
     */
    close () {
      this.$emit('cancel')
    },
    ...mapActions('urlCensus', ['getUrlTargetingSettings']),
    ...mapActions('rankZoneCommon', ['getSession']),
    convertToOptions (item) {
      return {
        value: item._id,
        label: item.label
      }
    },
    isUrlNotValid () {
      let url = this.normalizeUrl(this.domain, this.url.path)
      const isDomainInvalid = this.isDomainDropdownHidden ? false : !this.domain
      const isUrlInvalid = !this.url.path || !validator.isURL(url, { require_tld: true }) || url.length > 2048
      return isDomainInvalid || isUrlInvalid
    },
    /**
     * process url before saving (if {@link #isDomainDropdownHidden} == false):
     * - remove redundant slash symbol
     * - add missed slash symbol
     * - add 'https://' prefix if needed
     * */
    normalizeUrl (domain, path) {
      if (typeof domain !== 'string' || typeof path !== 'string') {
        return ''
      }
      let url
      path = path.trim()
      if (!this.isDomainDropdownHidden) {
        domain = domain.trim()
        const checkedUrl = { domain, path }
        this.removeRedundantSlash(checkedUrl)
        this.addMissedSlash(checkedUrl)
        url = checkedUrl.domain + checkedUrl.path
      } else {
        url = path
      }
      if (!url.startsWith('http')) {
        url = 'https://' + url
      }
      return url
    },
    removeRedundantSlash (checkedUrl) {
      const isRedundantSlashPresent = function () {
        return (checkedUrl.domain.endsWith('/') && checkedUrl.path.startsWith('/')) || checkedUrl.path.startsWith('//')
      }
      if (isRedundantSlashPresent()) {
        checkedUrl.path = checkedUrl.path.slice(1)
        if (isRedundantSlashPresent()) {
          this.removeRedundantSlash(checkedUrl)
        }
      }
    },
    addMissedSlash (checkedUrl) {
      if (!checkedUrl.domain.endsWith('/') && !checkedUrl.path.startsWith('/')) {
        checkedUrl.path = '/' + checkedUrl.path
      }
    },
    /**
     * check whether this url exist based on this.urls data
     * */
    isUrlAlreadyExist () {
      const url = this.normalizeUrl(this.domain, this.url.path)
      return this.urls.some(item => item === url)
    },
    isFormInvalid () {
      const nonMandatoryFields = [ 'date', 'sectionId', 'pageTypeId' ]
      if (this.isDomainDropdownHidden) {
        nonMandatoryFields.push('domain')
      }
      const fieldNotValid = Object.entries(this.url).some(([key, value]) => !nonMandatoryFields.includes(key) && !value)
      return fieldNotValid || this.isUrlNotValid() || this.isUrlAlreadyExist()
    },
    /**
     * Confirm new URL creation
     * IF form is valid popup emit event to {@link ./UrlPlanning} with new url
     */
    showError (error) {
      this.externalError = error
    },
    apply () {
      this.isValidating = true
      if (this.isFormInvalid()) {
        return
      }
      this.url.url = this.normalizeUrl(this.domain, this.url.path)
      this.$emit('apply', this.url)
    },
    calculateDate (date) {
      if (!date) return ''
      return new Date(date)
    },
    calculateElementWidth (el) {
      return el.offsetWidth
    }
  },
  filters: {
    ...Filters
  },
  watch: {
    clientId: {
      async handler (v) {
        if (v) {
          const queries = []
          queries.push(this.getUrlTargetingSettings(this.clientId))
          queries.push(this.getSession({ clientId: this.clientId }))
          await Promise.all(queries)
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
  .create-url {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 40px;

    &__page-type,
    &__section {
      max-width: 150px;
    }

    &__status {
      color: $rfx-medium-march;
      margin-top: 12px;
    }

    &__title-wrapper {
      display: flex;
      justify-content: space-evenly;
      height: 60px;
    }

    &__title {
      text-align: center;
      font-size: 34px;
      font-weight: 500;
      margin: 9px 0 35px;
      color: $rfx-dark-march;
    }
    &__header {
      background-color: rgba(1, 22, 39, 0.1);
      height: 120px;
      display: flex;
      flex-direction: column;
      margin: 0 -40px;
      padding-right: 40px;
      padding-left: 40px;
    }
    &__header-title {
      color: $rfx-dark-march;
      font-weight: bold;
      padding: 22px 0 5px;
      border: none;
      display: flex;
      align-items: flex-end;
      /*margin-bottom: 10px;*/
      margin-top: 10px;

    }
    &__info-item {
      color: $rfx-light-march;
      border: none;
      padding: 5px 10px 22px;

    }
    &__input {
      display: flex;
      flex-direction: column;
      position: relative;
      margin-top: 30px;

      &_invalid[type='text'] {
        border: 1px solid $rfx-red;
        border-radius: 4px;
      }
    }

    &__label {
      display: block;
      font-weight: 600;
      margin-bottom: 5px;
      color: $rfx-dark-march;
    }

    &__domain {
      border: 2px solid #e7e9eb;
      outline: none;
      border-radius: 4px;
      height: 40px;
      display: flex;
      align-items: center;
      padding-left: 20px;
      background: rgba(1, 22, 39, 0.1);
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
    }
    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;

        &:disabled {
          pointer-events: none;
          background-color: #fff;
          color: $rfx-neutral-grey;
          cursor: default;
        }
      }

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
      }
    }
    &__link {
      &-wrapper {
        position: relative;
        margin-top: 30px;

        &_single{
          margin-top: 0;
        }
      }

      &-field[type='text'] {
        width: 100%;
        box-sizing: border-box;
        padding-right: 60px;
        font-weight: 500;
        @include ellipsis;
      }
    }

    &__date {
      margin-top: 2px;
    }

    &__error-message {
        color: $rfx-red;
        font-size: 10px;
        margin-top: 5px;
      }

  }

  .form-control {
    height: 50px;
    padding: 0 20px;
    border: 2px solid $rfx-light-steel;
    outline: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    color: $rfx-dark-march;

    &::placeholder {
      color: $rfx-pearl;
      font-weight: normal;
      font-size: 14px;
    }

    &:disabled {
      background-color: $rfx-light-steel;
    }

    &_error{
       border-color: $rfx-red;
      color: $rfx-red;

       &::placeholder {
         color: $rfx-red;
         font-size: 13px;
       }

     }
  }

  .input-date {
    border: none;
    box-shadow: none;
    text-decoration: underline;
    background-color: transparent;
    padding: 0;
    color: $rfx-bright-blue;
    font-size: 13px;
    width: 80px;
    vertical-align: top;

    &::placeholder {
      color: $rfx-bright-blue;
      text-decoration: underline;
      font-size: 13px;
    }

    &_error {
      &::placeholder {
        color: $rfx-red;
        text-decoration: underline;
        font-size: 13px;
      }
    }
  }

</style>
